import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
// Import direto (não pelo barrel `@/shared/components/ui`): o barrel exporta
// este arquivo, e passar por ele fecharia um ciclo de import.
import { Input } from './Input';

export interface SearchableSelectOption {
  id: string;
  label: string;
  sublabel?: string;
  icon?: IconName;
}

export interface SearchableSelectProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Elemento que ancora o popover (o botão-pílula do header). */
  anchorRef: React.RefObject<HTMLElement | null>;
  options: SearchableSelectOption[];
  selectedId?: string | null;
  onSelect: (id: string) => void;
  /** Busca CONTROLADA de fora — quem chama aplica `useDebouncedValue` antes da query. */
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  isLoading?: boolean;
  searchPlaceholder: string;
  emptyLabel: string;
  /** Rodapé de aviso, ex.: "mostrando os 100 primeiros — busque pelo nome". */
  truncatedHint?: string;
  /** Ações abaixo da lista ("Criar cliente", "Desvincular"). Clicar fecha o painel. */
  footer?: React.ReactNode;
  /** `sheet` = folha de baixo do celular; `popover` = ancorado no gatilho. */
  variant?: 'popover' | 'sheet';
  testIdPrefix: string;
}

const PANEL_W = 288;

/**
 * Variáveis de tema em CLARO FIXO (regra do projeto para modal/drawer/dropdown).
 * O `Input` shared lê `var(--theme-input-*)`; declarar aqui no container faz
 * ele ficar claro dentro do painel mesmo com o app no tema escuro.
 */
const LIGHT_VARS = {
  '--theme-text': '#1e293b',
  '--theme-muted-text': '#6b7280',
  '--theme-input-background': '#ffffff',
  '--theme-input-text': '#1e293b',
  '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
  '--theme-input-focus': '#8b5cf6',
  '--theme-error-color': '#ef4444',
  '--theme-card-background': '#ffffff',
  '--theme-card-text': '#334155',
  '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
  '--theme-primary': '#8b5cf6',
} as React.CSSProperties;

/**
 * Seleção ÚNICA com busca, em portal. Nasceu para o seletor de Cliente do header
 * do chat, mas é o primitivo compartilhado de "select único com busca" — papel
 * que faltava no design system (o `Select` é nativo e sem busca, o `MultiSelect`
 * é múltiplo, o `ContextSelector` não busca).
 *
 * A mecânica de posicionamento é a do `ContextSelector`: recalcula em `resize` e
 * `scroll` (captura), porque o header vive dentro de container rolável.
 */
export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  open,
  onOpenChange,
  anchorRef,
  options,
  selectedId,
  onSelect,
  searchTerm,
  onSearchTermChange,
  isLoading = false,
  searchPlaceholder,
  emptyLabel,
  truncatedHint,
  footer,
  variant = 'popover',
  testIdPrefix,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const [highlight, setHighlight] = useState(0);

  // Posição do popover — clampada à viewport, com virada para cima quando não
  // cabe embaixo. `useLayoutEffect` roda com o painel já no DOM, então
  // `panelRef.current.offsetHeight` é a altura real e não há repintura torta.
  const computePos = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const panelH = panelRef.current?.offsetHeight ?? 0;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_W - 8));
    const abaixo = rect.bottom + 6;
    const semEspacoAbaixo = panelH > 0 && abaixo + panelH > window.innerHeight - 8;
    const cabeAcima = rect.top - 6 - panelH > 8;
    setPos({ left, top: semEspacoAbaixo && cabeAcima ? rect.top - 6 - panelH : abaixo });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open || variant === 'sheet') return;
    computePos();
    window.addEventListener('resize', computePos);
    window.addEventListener('scroll', computePos, true);
    return () => {
      window.removeEventListener('resize', computePos);
      window.removeEventListener('scroll', computePos, true);
    };
  }, [open, variant, computePos]);

  // Clique-fora ciente do portal. O GATILHO conta como "dentro": senão o
  // `mousedown` fecharia e o `click` do próprio botão reabriria em seguida.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (panelRef.current?.contains(alvo)) return;
      if (anchorRef.current?.contains(alvo)) return;
      onOpenChange(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, anchorRef, onOpenChange]);

  // Teclado: setas navegam (com volta nas pontas), Enter escolhe, Esc fecha.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onOpenChange(false);
        return;
      }
      if (options.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((i) => (i + 1) % options.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((i) => (i - 1 + options.length) % options.length);
        return;
      }
      if (e.key === 'Enter') {
        const escolhida = options[highlight];
        if (!escolhida) return;
        e.preventDefault();
        onSelect(escolhida.id);
        onOpenChange(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, options, highlight, onSelect, onOpenChange]);

  // Lista nova (abriu, buscou) volta o destaque para o topo.
  useEffect(() => {
    setHighlight(0);
  }, [open, searchTerm, options.length]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${highlight}"]`);
    // jsdom não implementa scrollIntoView — a guarda mantém o teste unitário vivo.
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [open, highlight]);

  if (!open) return null;

  const isSheet = variant === 'sheet';

  const conteudo = (
    <>
      <div className="p-2" style={{ borderBottom: '1px solid #e5e7eb' }}>
        <Input
          autoFocus
          icon="search"
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          placeholder={searchPlaceholder}
          data-testid={`${testIdPrefix}-search`}
        />
      </div>

      <div
        ref={listRef}
        className="overflow-y-auto p-1.5"
        style={{ maxHeight: isSheet ? '45vh' : 288 }}
        role="listbox"
      >
        {isLoading && (
          <p
            className="px-2.5 py-3"
            style={{ color: '#94a3b8', fontSize: isSheet ? 13 : 12 }}
            data-testid={`${testIdPrefix}-loading`}
          >
            Carregando…
          </p>
        )}

        {!isLoading && options.length === 0 && (
          <p
            className="px-2.5 py-3"
            style={{ color: '#94a3b8', fontSize: isSheet ? 13 : 12 }}
            data-testid={`${testIdPrefix}-empty`}
          >
            {emptyLabel}
          </p>
        )}

        {!isLoading &&
          options.map((option, index) => {
            const ativo = option.id === selectedId;
            const destacado = index === highlight;
            return (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={ativo}
                data-idx={index}
                data-testid={`${testIdPrefix}-option-${option.id}`}
                onClick={() => {
                  onSelect(option.id);
                  onOpenChange(false);
                }}
                onMouseEnter={() => setHighlight(index)}
                className={`w-full flex items-center gap-2 px-2.5 rounded-lg text-left ${
                  isSheet ? 'min-h-[44px] py-2' : 'py-2'
                }`}
                style={{
                  color: '#374151',
                  backgroundColor: ativo ? '#f3e8ff' : destacado ? '#f1f5f9' : 'transparent',
                }}
              >
                {option.icon && (
                  <Icon
                    name={option.icon}
                    className="w-4 h-4 flex-shrink-0"
                    style={{ color: '#64748b' }}
                  />
                )}
                <span className="flex-1 min-w-0">
                  <span
                    className="block truncate font-medium"
                    style={{ fontSize: isSheet ? 15 : 13 }}
                  >
                    {option.label}
                  </span>
                  {option.sublabel && (
                    <span
                      className="block truncate"
                      style={{ fontSize: isSheet ? 13 : 12, color: '#94a3b8' }}
                    >
                      {option.sublabel}
                    </span>
                  )}
                </span>
                {ativo && (
                  <Icon
                    name="check"
                    className="w-4 h-4 flex-shrink-0"
                    style={{ color: '#8b5cf6' }}
                  />
                )}
              </button>
            );
          })}
      </div>

      {truncatedHint && (
        <p
          className="px-3 py-2"
          style={{ borderTop: '1px solid #f1f5f9', color: '#94a3b8', fontSize: 12 }}
          data-testid={`${testIdPrefix}-hint`}
        >
          {truncatedHint}
        </p>
      )}

      {footer && (
        <div
          className="p-1.5"
          style={{ borderTop: '1px solid #f1f5f9' }}
          // Ação de rodapé leva para fora da lista (modal/desvincular) — fecha o painel.
          onClick={() => onOpenChange(false)}
        >
          {footer}
        </div>
      )}
    </>
  );

  if (isSheet) {
    return createPortal(
      <div className="fixed inset-0 z-[100]">
        <div
          className="absolute inset-0"
          style={{ backgroundColor: 'rgba(15,23,42,0.45)' }}
          // Evento sintético do React borbulha ATRAVÉS do portal — sem o
          // stopPropagation o clique reabriria o painel no ancestral.
          onClick={(e) => {
            e.stopPropagation();
            onOpenChange(false);
          }}
          aria-hidden
        />
        <div
          ref={panelRef}
          className="absolute inset-x-0 bottom-0 rounded-t-2xl pb-[calc(env(safe-area-inset-bottom,0px)+8px)]"
          style={{ backgroundColor: '#ffffff', ...LIGHT_VARS }}
          data-testid={`${testIdPrefix}-panel`}
          data-variant="sheet"
        >
          <div className="flex justify-center pt-2 pb-1">
            <span className="w-9 h-1 rounded-full" style={{ backgroundColor: '#e2e8f0' }} />
          </div>
          {conteudo}
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      ref={panelRef}
      className="fixed z-[100] rounded-xl overflow-hidden"
      style={{
        left: pos.left,
        top: pos.top,
        width: PANEL_W,
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        boxShadow: '0 4px 24px rgba(0,0,0,.12)',
        ...LIGHT_VARS,
      }}
      data-testid={`${testIdPrefix}-panel`}
      data-variant="popover"
    >
      {conteudo}
    </div>,
    document.body
  );
};

SearchableSelect.displayName = 'SearchableSelect';
